import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: { width: "100%", maxWidth: 1000, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: theme.app.ink },
  subtitle: { color: theme.app.textTertiary, fontSize: 13, marginTop: 4, marginBottom: theme.spacing(2.5) },
  cards: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: theme.spacing(2.5), [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
  card: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, padding: "16px 18px" },
  cardLbl: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.app.textTertiary, fontWeight: 600 },
  cardVal: { fontFamily: theme.app.mono, fontSize: 24, fontWeight: 600, color: theme.app.ink, marginTop: 6 },
  cardHint: { fontSize: 11.5, color: theme.app.textTertiary, marginTop: 4 },
  group: { fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: theme.app.textTertiary, margin: theme.spacing(2.5, 0, 1) },
  tbl: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, overflow: "hidden" },
  row: { display: "grid", gridTemplateColumns: "1fr 140px 120px 130px", alignItems: "center", height: 48, padding: "0 16px", borderBottom: `1px solid ${theme.app.lineSoft}`, "&:last-child": { borderBottom: "none" } },
  name: { fontSize: 13, color: theme.app.ink, display: "flex", alignItems: "center", gap: 8 },
  qty: { fontFamily: theme.app.mono, fontSize: 12.5, color: theme.app.textSecondary, textAlign: "right" },
  price: { fontFamily: theme.app.mono, fontSize: 11.5, color: theme.app.textTertiary, textAlign: "right" },
  amount: { fontFamily: theme.app.mono, fontSize: 13, color: theme.app.ink, textAlign: "right", fontWeight: 500 },
  badge: { fontFamily: theme.app.mono, fontSize: 10, padding: "1px 6px", borderRadius: 4, background: theme.app.lineSoft, color: theme.app.textTertiary },
  // ---- cabeçalho do plano ----
  planCard: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 12, padding: "16px 18px", marginBottom: theme.spacing(2.5), display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" },
  planName: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 18, color: theme.app.ink },
  planMeta: { fontSize: 12.5, color: theme.app.textSecondary, marginTop: 2 },
  planBtn: { marginLeft: "auto", textTransform: "none", fontWeight: 600, fontSize: 13, background: "#2C353D", color: "#fff", borderRadius: 8, padding: "7px 16px", boxShadow: "none", "&:hover": { background: "#1f262c", boxShadow: "none" } },
  // ---- barras de franquia ----
  bars: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 12, padding: "16px 18px", marginBottom: theme.spacing(2.5) },
  barRow: { marginBottom: theme.spacing(1.75), "&:last-child": { marginBottom: 0 } },
  barHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 },
  barLbl: { fontSize: 13, color: theme.app.ink, fontWeight: 500 },
  barVal: { fontFamily: theme.app.mono, fontSize: 12, color: theme.app.textTertiary },
  over: { color: theme.app.danger, fontWeight: 600 },
  barTrack: { height: 8, borderRadius: 999, background: theme.app.lineSoft, overflow: "hidden" },
  barFill: { height: "100%", borderRadius: 999, transition: "width .3s ease" },
}));

const brl = cents => `R$ ${((cents || 0) / 100).toFixed(2).replace(".", ",")}`;
const fmtQty = (q, unit) => `${(q || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} ${unit}`;

// PORTE do plano em IA, sem expor a contagem de tokens: descreve o que a
// franquia comporta, não o quanto já foi gasto. Uma conta Pro que não usou nada
// continua "Intenso" — é o plano que é intenso.
//
// Deriva do valor da franquia, não do código do plano: plano novo entra sozinho
// na faixa certa, e plano customizado por conta (limits próprio) também.
// O quanto já foi consumido continua visível na barra e no aviso de excedente.
const CAPACITY = [
  { min: 500000, label: "Intenso" }, // Starter: 500k · Pro: 2M
  { min: 300000, label: "Moderado" }, // Staff: 300k
  { min: 0, label: "Leve" }, // Grátis: 100k
];
const capacityLabel = limit => {
  if (limit == null) return "Ilimitado"; // sem franquia — hoje não chega a renderizar (push ignora)
  const faixa = CAPACITY.find(c => limit >= c.min);
  return faixa ? faixa.label : "Leve";
};
const fmtNum = n => (n || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 });
const fmtDate = iso => {
  try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }); } catch (e) { return "—"; }
};

const Usage = () => {
  const classes = useStyles();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null); // { plan, cycle, limits, usage }
  const [balance, setBalance] = useState(null); // { configured, saldo }

  const load = useCallback(async () => {
    try {
      const [u, p, b] = await Promise.all([
        api.get("/usage").catch(() => ({ data: null })),
        api.get("/me/plan").catch(() => ({ data: null })),
        api.get("/me/balance").catch(() => ({ data: null })),
      ]);
      setData(u.data);
      setPlan(p.data);
      setBalance(b.data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // barras de franquia do ciclo (a partir de /me/plan). IA vira INTENSIDADE.
  const limits = (plan && plan.limits) || {};
  const usage = (plan && plan.usage) || {};
  const bars = [];
  if (plan) {
    const push = (label, used, limit, unit, aiIntensity) => {
      if (limit == null) return; // ilimitado → não mostra barra
      const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
      // `pct` é limitado a 100 pra barra não estourar — então ele sozinho não
      // distingue "no limite" de "muito além dele". `over` guarda esse fato.
      bars.push({ label, used, limit, unit, pct, aiIntensity, over: limit > 0 && used > limit });
    };
    push("IA / Copiloto", usage["ai.tokens"] || 0, limits.aiTokens, "tok", true);
    push("Execuções de fluxo", usage["flow.execution"] || 0, limits.flowExecutionsPerMonth, "exec");
    push("Nós executados", usage["node.execution"] || 0, limits.nodeExecutionsPerMonth, "nós");
    push("Agente de voz (teste)", usage["agent.voice"] || 0, limits.agentTestMinutes, "min");
    push("Mensagens WhatsApp", usage["whatsapp.message"] || 0, limits.whatsappTemplates, "msg");
    push("Mensagens SMS", usage["sms.message"] || 0, limits.smsPerMonth, "msg");
  }

  const meters = (data && data.meters) || [];
  const overage = (plan && plan.overage) || { items: [], totalCents: 0 };
  const overageItems = overage.items || [];
  const overageCents = overage.totalCents || 0;

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <Typography className={classes.title}>Uso & Saldo</Typography>
        <Typography className={classes.subtitle}>
          Consumo do mês corrente por medidor. A numera tarifa e debita do seu saldo pré-pago.
        </Typography>

        {plan && plan.plan && (
          <div className={classes.planCard}>
            <div>
              <div className={classes.planName}>Plano {plan.plan.name}</div>
              <div className={classes.planMeta}>
                Ciclo {fmtDate(plan.cycle && plan.cycle.start)} – {fmtDate(plan.cycle && plan.cycle.end)} · renova em {fmtDate(plan.cycle && plan.cycle.end)}
              </div>
            </div>
            <Button className={classes.planBtn} onClick={() => history.push("/plans")}>Gerenciar plano</Button>
          </div>
        )}

        {bars.length > 0 && (
          <div className={classes.bars}>
            <div className={classes.group} style={{ marginTop: 0 }}>Franquias do ciclo</div>
            {bars.map(b => {
              const color = b.pct >= 85 ? "#C0453B" : b.pct >= 50 ? "#C77A12" : "#86C240";
              return (
                <div key={b.label} className={classes.barRow}>
                  <div className={classes.barHead}>
                    <span className={classes.barLbl}>{b.label}</span>
                    <span className={classes.barVal}>
                      {b.aiIntensity ? capacityLabel(b.limit) : `${fmtNum(b.used)} / ${fmtNum(b.limit)} ${b.unit}`}
                      {/* o rótulo de porte não diz nada sobre consumo — sem isto,
                          estourar a franquia fica invisível na barra de IA. */}
                      {b.over && <span className={classes.over}> · franquia excedida</span>}
                    </span>
                  </div>
                  <div className={classes.barTrack}>
                    <div className={classes.barFill} style={{ width: `${b.pct}%`, background: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className={classes.cards} style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
          <div className={classes.card}>
            <div className={classes.cardLbl}>Excedente do ciclo</div>
            <div className={classes.cardVal} style={{ color: overageCents > 0 ? undefined : "#1E9E62" }}>
              {brl(overageCents)}
            </div>
            <div className={classes.cardHint}>
              {overageCents > 0
                ? "só o que passou da franquia — o incluído no plano não é cobrado"
                : "tudo dentro do plano · sem excedente"}
            </div>
          </div>
          <div className={classes.card}>
            <div className={classes.cardLbl}>Saldo BR DID</div>
            <div className={classes.cardVal} style={{ color: balance && balance.configured && balance.saldo != null ? undefined : "#929AA4" }}>
              {balance && balance.configured && balance.saldo != null ? brl(balance.saldo * 100) : "—"}
            </div>
            <div className={classes.cardHint}>
              {balance && balance.configured ? "a carteira pré-paga (BR DID)" : "disponível ao conectar o BR DID"}
            </div>
          </div>
        </div>

        {/* excedente detalhado — só a parte acima da franquia */}
        {overageItems.length > 0 && (
          <div>
            <div className={classes.group}>Excedente do ciclo</div>
            <div className={classes.tbl}>
              {overageItems.map(it => (
                <div key={it.key} className={classes.row}>
                  <span className={classes.name}>{it.label}</span>
                  <span className={classes.qty}>+{fmtNum(it.over)} {it.unit}</span>
                  <span className={classes.price}>além da franquia</span>
                  <span className={classes.amount}>{brl(it.cents)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* consumo pré-pago (voz/repasse) — sai do saldo por uso, não é franquia */}
        <div className={classes.group}>Consumo pré-pago (do saldo)</div>
        <div className={classes.tbl}>
          {meters.filter(m => m.quantity > 0).length === 0 ? (
            <div className={classes.row} style={{ color: "#929AA4" }}>Nenhum consumo pré-pago neste ciclo.</div>
          ) : (
            meters
              .filter(m => m.quantity > 0)
              .map(m => (
                <div key={m.key} className={classes.row}>
                  <span className={classes.name}>
                    {m.label}
                    {m.brDidBilled && <span className={classes.badge}>repasse BR DID</span>}
                  </span>
                  <span className={classes.qty}>{fmtQty(m.quantity, m.unit)}</span>
                  <span className={classes.price}>{brl(m.priceCents)}/{m.unit}</span>
                  <span className={classes.amount}>{brl(m.amountCents)}</span>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Usage;
